import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API, formatApiError, runAiJob } from "../lib/api";
import { EnforcementBadge } from "./ui-helpers";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Plus, Trash, PencilSimple, DownloadSimple, UploadSimple, CaretDown, CaretRight } from "@phosphor-icons/react";

const STATUSES = [
  ["currently_enforceable", "Currently Enforceable"],
  ["future_enforceable", "Future Enforceable"],
  ["superseded", "Superseded"],
  ["retired", "Retired"],
];
const emptyStd = { standard_id: "", title: "", category: "", description: "", purpose: "", status: "currently_enforceable", effective_date: "" };
const emptyReq = { standard_id: "", req_number: "", text: "", measure: "", expected_evidence: [], sub_requirements: [], status: "currently_enforceable", effective_date: "" };

export default function StandardsManager() {
  const [standards, setStandards] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [stdDialog, setStdDialog] = useState(false);
  const [reqDialog, setReqDialog] = useState(false);
  const [stdForm, setStdForm] = useState(emptyStd);
  const [reqForm, setReqForm] = useState(emptyReq);
  const [stdEditId, setStdEditId] = useState(null);
  const [reqEditId, setReqEditId] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const measFileRef = useRef(null);
  const [harvesting, setHarvesting] = useState(false);
  const [measStaging, setMeasStaging] = useState(null);
  const [committingMeas, setCommittingMeas] = useState(false);
  const stdPdfRef = useRef(null);
  const [stdPdfImporting, setStdPdfImporting] = useState(false);
  const [stdReview, setStdReview] = useState(null);   // { engine, filename, exists, parsed }
  const [committingStd, setCommittingStd] = useState(false);

  const load = () => Promise.all([api.get("/standards"), api.get("/requirements")]).then(([s, r]) => { setStandards(s.data); setReqs(r.data); });
  useEffect(() => { load(); }, []);

  const reqsByStd = useMemo(() => {
    const m = {};
    reqs.forEach((r) => { (m[r.standard_id] || (m[r.standard_id] = [])).push(r); });
    return m;
  }, [reqs]);

  // ---- import / template ----
  const downloadTemplate = () => window.open(`${API}/standards/import-template`, "_blank");
  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/standards/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const extra = data.skipped_inactive ? `, ${data.skipped_inactive} inactive skipped` : "";
      toast.success(`Imported: +${data.standards_added} standards (~${data.standards_updated} updated), +${data.requirements_added} requirements${extra}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  // ---- measures harvesting ----
  const onHarvestMeasures = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setHarvesting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/measures/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const jobId = data.job_id;
      toast.message("Harvesting measures… this can take a few minutes for a full standards set.");
      const poll = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/ai/jobs/${jobId}`);
          if (j.status === "done") {
            clearInterval(poll); setHarvesting(false);
            const r = await api.get(`/measures/staging/${j.result.staging_id}`);
            setMeasStaging(r.data);
          } else if (j.status === "error") {
            clearInterval(poll); setHarvesting(false); toast.error("Measures harvest failed: " + (j.error || ""));
          }
        } catch { clearInterval(poll); setHarvesting(false); }
      }, 5000);
    } catch (err) { setHarvesting(false); toast.error(formatApiError(err.response?.data?.detail)); }
    finally { if (measFileRef.current) measFileRef.current.value = ""; }
  };
  const commitMeasures = async () => {
    if (!measStaging) return;
    setCommittingMeas(true);
    try {
      const { data } = await api.post(`/measures/staging/${measStaging.id}/commit`, {});
      toast.success(`Applied measures to ${data.applied_requirements} requirement(s) + ${data.applied_subparts} part(s)`);
      setMeasStaging(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCommittingMeas(false); }
  };

  // ---- single-standard PDF import (one standard at a time) ----
  const onImportStandardPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStdPdfImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      toast.message("Parsing standard PDF… extracting requirements, measures, attachments & guidance.");
      const data = await runAiJob("/standards/import-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStdReview({ ...data, overwrite: true });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Standard PDF parse failed."); }
    finally { setStdPdfImporting(false); if (stdPdfRef.current) stdPdfRef.current.value = ""; }
  };
  const patchParsed = (patch) => setStdReview((r) => ({ ...r, parsed: { ...r.parsed, ...patch } }));
  const patchReq = (i, patch) => setStdReview((r) => {
    const requirements = r.parsed.requirements.map((x, k) => (k === i ? { ...x, ...patch } : x));
    return { ...r, parsed: { ...r.parsed, requirements } };
  });
  const removeReq = (i) => setStdReview((r) => ({ ...r, parsed: { ...r.parsed, requirements: r.parsed.requirements.filter((_, k) => k !== i) } }));
  const commitStandardPdf = async () => {
    if (!stdReview) return;
    setCommittingStd(true);
    try {
      const af = stdReview.parsed.applicable_functions;
      const payload = {
        ...stdReview.parsed,
        applicable_functions: typeof af === "string" ? af.split(/[,\n]/).map((x) => x.trim()).filter(Boolean) : af,
        effective_date: stdReview.parsed.effective_date || null,
        overwrite: stdReview.overwrite !== false,
      };
      const { data } = await api.post("/standards/import-pdf/commit", payload);
      toast.success(`${data.standard_id} ${data.updated ? "updated" : "created"} — ${data.requirements} requirement(s), ${data.attachments} attachment(s)`);
      setStdReview(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed."); }
    finally { setCommittingStd(false); }
  };

  // ---- standard CRUD ----
  const openNewStd = () => { setStdForm(emptyStd); setStdEditId(null); setStdDialog(true); };
  const openEditStd = (s) => { setStdForm({ ...emptyStd, ...s, effective_date: s.effective_date || "" }); setStdEditId(s.id); setStdDialog(true); };
  const saveStd = async () => {
    if (!stdForm.standard_id.trim()) return toast.error("Standard ID required");
    const payload = { ...stdForm, effective_date: stdForm.effective_date || null };
    try {
      if (stdEditId) await api.put(`/standards/${stdEditId}`, payload);
      else await api.post("/standards", payload);
      setStdDialog(false); load(); toast.success("Standard saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteStd = async (s) => {
    if (!window.confirm(`Delete ${s.standard_id} and all its requirements?`)) return;
    await api.delete(`/standards/${s.id}`); load(); toast.success("Standard deleted");
  };

  // ---- requirement CRUD ----
  const openNewReq = (stdId) => { setReqForm({ ...emptyReq, standard_id: stdId }); setReqEditId(null); setReqDialog(true); };
  const openEditReq = (r) => { setReqForm({ ...emptyReq, ...r, effective_date: r.effective_date || "" }); setReqEditId(r.id); setReqDialog(true); };
  const saveReq = async () => {
    if (!reqForm.req_number.trim()) return toast.error("Requirement number required");
    const payload = {
      ...reqForm, effective_date: reqForm.effective_date || null,
      sub_requirements: (reqForm.sub_requirements || []).map((sp) => ({ ...sp, effective_date: sp.effective_date || null })),
      expected_evidence: typeof reqForm.expected_evidence === "string"
        ? reqForm.expected_evidence.split(/[;\n]/).map((x) => x.trim()).filter(Boolean)
        : reqForm.expected_evidence,
    };
    try {
      if (reqEditId) await api.put(`/requirements/${reqEditId}`, payload);
      else await api.post("/requirements", payload);
      setReqDialog(false); load(); toast.success("Requirement saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteReq = async (r) => { await api.delete(`/requirements/${r.id}`); load(); };

  // ---- attachment CRUD (standard-level binding Attachments) ----
  const emptyAtt = { label: "", title: "", text: "", effective_date: "" };
  const [attDialog, setAttDialog] = useState(false);
  const [attForm, setAttForm] = useState(emptyAtt);
  const [attEditId, setAttEditId] = useState(null);
  const [attStdId, setAttStdId] = useState(null);
  const openNewAtt = (stdId) => { setAttStdId(stdId); setAttForm(emptyAtt); setAttEditId(null); setAttDialog(true); };
  const openEditAtt = (stdId, a) => { setAttStdId(stdId); setAttForm({ ...emptyAtt, ...a, effective_date: a.effective_date || "" }); setAttEditId(a.id); setAttDialog(true); };
  const saveAtt = async () => {
    if (!attForm.label.trim()) return toast.error("Attachment label required (e.g. Attachment 1)");
    const payload = { ...attForm, effective_date: attForm.effective_date || null };
    try {
      if (attEditId) await api.put(`/standards/${attStdId}/attachments/${attEditId}`, payload);
      else await api.post(`/standards/${attStdId}/attachments`, payload);
      setAttDialog(false); load(); toast.success("Attachment saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteAtt = async (stdId, a) => {
    if (!window.confirm(`Delete "${a.label}"?`)) return;
    await api.delete(`/standards/${stdId}/attachments/${a.id}`); load(); toast.success("Attachment deleted");
  };

  // ---- requirement Parts / Sections editor ----
  const parts = () => reqForm.sub_requirements || [];
  const setParts = (next) => setReqForm({ ...reqForm, sub_requirements: next });
  const addPart = () => setParts([...parts(), { label: "", text: "", impact_levels: [], functions: [], effective_date: "" }]);
  const updatePart = (i, key, val) => setParts(parts().map((p, idx) => (idx === i ? { ...p, [key]: val } : p)));
  const removePart = (i) => setParts(parts().filter((_, idx) => idx !== i));
  const togglePartImpact = (i, im) => setParts(parts().map((p, idx) => {
    if (idx !== i) return p;
    const cur = p.impact_levels || [];
    return { ...p, impact_levels: cur.includes(im) ? cur.filter((x) => x !== im) : [...cur, im] };
  }));

  return (
    <div className="space-y-4" data-testid="standards-manager">
      <div className="flex flex-wrap items-center gap-2 border border-border bg-secondary/30 p-3">
        <span className="overline text-muted-foreground mr-2">Bulk load</span>
        <Button data-testid="download-template-btn" onClick={downloadTemplate} variant="outline" className="rounded-none"><DownloadSimple size={16} className="mr-1" />Download Excel Template</Button>
        <input ref={fileRef} type="file" accept=".xlsx,.xlsm" onChange={onImport} className="hidden" data-testid="import-file-input" />
        <Button data-testid="import-excel-btn" onClick={() => fileRef.current?.click()} disabled={importing} className="rounded-none bg-primary hover:bg-primary/90"><UploadSimple size={16} className="mr-1" />{importing ? "Importing…" : "Import Excel"}</Button>
        <span className="text-xs text-muted-foreground">Accepts the template above or NERC's official "Standards Subject to Future Enforcement" .xlsx.</span>
        <input ref={measFileRef} type="file" accept=".pdf" onChange={onHarvestMeasures} className="hidden" data-testid="harvest-measures-input" />
        <Button data-testid="harvest-measures-btn" onClick={() => measFileRef.current?.click()} disabled={harvesting} variant="outline" className="rounded-none">
          <UploadSimple size={16} className="mr-1" />{harvesting ? "Harvesting…" : "Harvest Measures (PDF)"}
        </Button>
        <div className="w-px self-stretch bg-border mx-1" />
        <span className="overline text-muted-foreground mr-1">One at a time</span>
        <input ref={stdPdfRef} type="file" accept=".pdf" onChange={onImportStandardPdf} className="hidden" data-testid="import-standard-pdf-input" />
        <Button data-testid="import-standard-pdf-btn" onClick={() => stdPdfRef.current?.click()} disabled={stdPdfImporting} className="rounded-none bg-[#071B3E] hover:bg-[#0c2a5e]">
          <UploadSimple size={16} className="mr-1" />{stdPdfImporting ? "Parsing…" : "Upload Standard PDF"}
        </Button>
        <div className="flex-1" />
        <Button data-testid="add-standard-btn" onClick={openNewStd} variant="outline" className="rounded-none"><Plus size={16} className="mr-1" />Add Standard</Button>
      </div>

      <div className="border border-border bg-card divide-y divide-border">
        {standards.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No standards yet. Import an Excel file or add one manually.</div>}
        {standards.map((s) => {
          const list = reqsByStd[s.standard_id] || [];
          const open = expanded[s.id];
          return (
            <div key={s.id} data-testid={`std-manage-${s.standard_id}`}>
              <div className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/30">
                <button onClick={() => setExpanded({ ...expanded, [s.id]: !open })} data-testid={`expand-${s.standard_id}`} className="text-muted-foreground">
                  {open ? <CaretDown size={16} /> : <CaretRight size={16} />}
                </button>
                <span className="font-mono font-bold text-sm bg-primary text-primary-foreground px-2 py-0.5">{s.standard_id}</span>
                <span className="flex-1 text-sm font-medium truncate">{s.title || <span className="text-muted-foreground italic">untitled</span>}</span>
                <span className="text-xs text-muted-foreground font-mono">{s.category}</span>
                <EnforcementBadge status={s.status} effectiveDate={s.effective_date} />
                <span className="text-xs text-muted-foreground font-mono w-16 text-right">{list.length} req</span>
                <button data-testid={`edit-std-${s.standard_id}`} onClick={() => openEditStd(s)} className="text-muted-foreground hover:text-foreground"><PencilSimple size={16} /></button>
                <button data-testid={`delete-std-${s.standard_id}`} onClick={() => deleteStd(s)} className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
              </div>
              {open && (
                <div className="bg-secondary/20 px-4 py-3 border-t border-border">
                  <div className="flex justify-between items-center mb-2">
                    <span className="overline text-muted-foreground">Requirements</span>
                    <Button data-testid={`add-req-${s.standard_id}`} onClick={() => openNewReq(s.standard_id)} variant="outline" size="sm" className="rounded-none h-7"><Plus size={14} className="mr-1" />Add Requirement</Button>
                  </div>
                  {list.length === 0 ? <p className="text-sm text-muted-foreground">No requirements.</p> : (
                    <div className="divide-y divide-border border border-border bg-card">
                      {list.map((r) => (
                        <div key={r.id} className="flex items-start gap-3 px-3 py-2">
                          <span className="font-mono font-bold text-sm w-10 shrink-0">{r.req_number}</span>
                          <div className="flex-1">
                            <p className="text-sm">{r.text || <span className="italic text-muted-foreground">no text</span>}</p>
                            {r.measure && <p className="text-xs text-emerald-800 bg-emerald-50 border-l-2 border-emerald-400 pl-2 py-0.5 mt-1" data-testid={`req-measure-${r.id}`}><span className="font-bold uppercase text-[10px] mr-1">Measure</span>{r.measure}</p>}
                            {r.sub_requirements?.length > 0 && <p className="text-xs text-muted-foreground mt-0.5">{r.sub_requirements.length} sub-requirement(s)</p>}
                          </div>
                          <EnforcementBadge status={r.status} effectiveDate={r.effective_date} />
                          <button data-testid={`edit-req-${r.id}`} onClick={() => openEditReq(r)} className="text-muted-foreground hover:text-foreground"><PencilSimple size={14} /></button>
                          <button onClick={() => deleteReq(r)} className="text-muted-foreground hover:text-red-600"><Trash size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex justify-between items-center mt-4 mb-2">
                    <span className="overline text-muted-foreground">Attachments (binding, standard-level)</span>
                    <Button data-testid={`add-att-${s.standard_id}`} onClick={() => openNewAtt(s.id)} variant="outline" size="sm" className="rounded-none h-7"><Plus size={14} className="mr-1" />Add Attachment</Button>
                  </div>
                  {(s.attachments || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground">No attachments. Add binding Attachments (e.g. CIP-003-9 Attachment 1) so STaR can compare them.</p>
                  ) : (
                    <div className="divide-y divide-border border border-violet-200 bg-violet-50/30" data-testid={`att-list-${s.standard_id}`}>
                      {(s.attachments || []).map((a) => (
                        <div key={a.id} data-testid={`att-${a.id}`} className="flex items-start gap-3 px-3 py-2">
                          <span className="font-mono font-bold text-sm text-violet-800 shrink-0">{a.label}</span>
                          <div className="flex-1 min-w-0">
                            {a.title && <p className="text-sm font-medium truncate">{a.title}</p>}
                            <p className="text-xs text-muted-foreground line-clamp-2">{a.text || <span className="italic">no text</span>}</p>
                          </div>
                          {a.effective_date && <span className="text-[10px] font-mono text-muted-foreground">eff {a.effective_date}</span>}
                          <button data-testid={`edit-att-${a.id}`} onClick={() => openEditAtt(s.id, a)} className="text-muted-foreground hover:text-foreground"><PencilSimple size={14} /></button>
                          <button data-testid={`delete-att-${a.id}`} onClick={() => deleteAtt(s.id, a)} className="text-muted-foreground hover:text-red-600"><Trash size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Standard dialog */}
      <Dialog open={stdDialog} onOpenChange={setStdDialog}>
        <DialogContent className="rounded-none">
          <DialogHeader><DialogTitle>{stdEditId ? "Edit" : "Add"} Standard</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Standard ID</Label><Input data-testid="std-id-input" value={stdForm.standard_id} onChange={(e) => setStdForm({ ...stdForm, standard_id: e.target.value })} placeholder="CIP-007-6" className="rounded-none mt-1" /></div>
              <div><Label>Family / Category</Label><Input value={stdForm.category} onChange={(e) => setStdForm({ ...stdForm, category: e.target.value })} placeholder="CIP" className="rounded-none mt-1" /></div>
            </div>
            <div><Label>Title</Label><Input value={stdForm.title} onChange={(e) => setStdForm({ ...stdForm, title: e.target.value })} className="rounded-none mt-1" /></div>
            <div><Label>Purpose</Label><Textarea data-testid="std-purpose-input" value={stdForm.purpose} onChange={(e) => setStdForm({ ...stdForm, purpose: e.target.value })} placeholder="The standard's Purpose statement (Section A.3)…" className="rounded-none mt-1 min-h-[80px]" /></div>
            <div><Label>Description</Label><Textarea value={stdForm.description} onChange={(e) => setStdForm({ ...stdForm, description: e.target.value })} className="rounded-none mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Enforcement Status</Label>
                <Select value={stdForm.status} onValueChange={(v) => setStdForm({ ...stdForm, status: v })}>
                  <SelectTrigger data-testid="std-status-select" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Effective Date</Label><Input type="date" value={stdForm.effective_date} onChange={(e) => setStdForm({ ...stdForm, effective_date: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <Button data-testid="save-std-btn" onClick={saveStd} className="rounded-none w-full bg-primary hover:bg-primary/90">Save Standard</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Requirement dialog */}
      <Dialog open={reqDialog} onOpenChange={setReqDialog}>
        <DialogContent className="rounded-none max-w-xl">
          <DialogHeader><DialogTitle>{reqEditId ? "Edit" : "Add"} Requirement — {reqForm.standard_id}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Requirement Number</Label><Input data-testid="req-number-input" value={reqForm.req_number} onChange={(e) => setReqForm({ ...reqForm, req_number: e.target.value })} placeholder="R1" className="rounded-none mt-1" /></div>
            <div><Label>Requirement Text</Label><Textarea data-testid="req-text-input" value={reqForm.text} onChange={(e) => setReqForm({ ...reqForm, text: e.target.value })} className="rounded-none mt-1 min-h-[100px]" /></div>
            <div><Label>Measure (evidence expectations — M#)</Label><Textarea data-testid="req-measure-input" value={reqForm.measure || ""} onChange={(e) => setReqForm({ ...reqForm, measure: e.target.value })} placeholder="The Measure language describing acceptable evidence (e.g. M1)…" className="rounded-none mt-1 min-h-[70px]" /></div>
            <div><Label>Expected Evidence (one per line or ; separated)</Label>
              <Textarea value={Array.isArray(reqForm.expected_evidence) ? reqForm.expected_evidence.join("\n") : reqForm.expected_evidence}
                onChange={(e) => setReqForm({ ...reqForm, expected_evidence: e.target.value })} className="rounded-none mt-1" /></div>
            <div className="border border-border p-3 bg-secondary/20">
              <div className="flex items-center justify-between">
                <Label>Requirement Parts / Sections</Label>
                <Button type="button" data-testid="add-part-btn" onClick={addPart} variant="outline" size="sm" className="rounded-none h-7"><Plus size={14} className="mr-1" />Add Part</Button>
              </div>
              <div className="space-y-2 mt-2 max-h-64 overflow-y-auto">
                {parts().map((sp, i) => (
                  <div key={i} data-testid={`part-edit-${i}`} className="border border-border bg-card p-2 space-y-1">
                    <div className="flex gap-2 items-center">
                      <Input data-testid={`part-label-${i}`} value={sp.label || ""} placeholder="1.1 / Section 1" onChange={(e) => updatePart(i, "label", e.target.value)} className="rounded-none h-8 w-36" />
                      <div className="flex gap-3 text-xs items-center flex-1">
                        {["high", "medium", "low"].map((im) => (
                          <label key={im} className="flex items-center gap-1 cursor-pointer capitalize"><Checkbox checked={(sp.impact_levels || []).includes(im)} onCheckedChange={() => togglePartImpact(i, im)} className="rounded-none" />{im}</label>
                        ))}
                      </div>
                      <button type="button" data-testid={`remove-part-${i}`} onClick={() => removePart(i)} className="text-muted-foreground hover:text-red-600"><Trash size={14} /></button>
                    </div>
                    <Textarea data-testid={`part-text-${i}`} value={sp.text || ""} placeholder="Part / Section requirement text" onChange={(e) => updatePart(i, "text", e.target.value)} className="rounded-none text-sm min-h-[56px]" />
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-muted-foreground whitespace-nowrap">Enforcement date</span>
                      <Input data-testid={`part-effective-date-${i}`} type="date" value={sp.effective_date || ""} onChange={(e) => updatePart(i, "effective_date", e.target.value)} className="rounded-none h-8 w-44" />
                      <span className="text-[10px] text-muted-foreground">blank = inherits the requirement's date</span>
                    </div>
                  </div>
                ))}
                {parts().length === 0 && <p className="text-xs text-muted-foreground">No parts yet. CIP requirement-table Parts (e.g. 1.1) and Attachment Sections go here.</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Status</Label>
                <Select value={reqForm.status} onValueChange={(v) => setReqForm({ ...reqForm, status: v })}>
                  <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Effective Date</Label><Input type="date" value={reqForm.effective_date} onChange={(e) => setReqForm({ ...reqForm, effective_date: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <Button data-testid="save-req-btn" onClick={saveReq} className="rounded-none w-full bg-primary hover:bg-primary/90">Save Requirement</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Measures harvest staging review */}
      <Dialog open={!!measStaging} onOpenChange={(o) => { if (!o) setMeasStaging(null); }}>
        <DialogContent className="rounded-none max-w-3xl">
          <DialogHeader><DialogTitle>Review Harvested Measures — {measStaging?.filename}</DialogTitle></DialogHeader>
          {measStaging && (
            <div className="space-y-3">
              <div className="flex gap-3 text-sm flex-wrap" data-testid="meas-staging-counts">
                <span className="border border-emerald-300 bg-emerald-50 text-emerald-700 px-2 py-1 font-semibold">{measStaging.counts.matched} matched to requirements</span>
                <span className="border border-indigo-300 bg-indigo-50 text-indigo-700 px-2 py-1 font-semibold">{measStaging.counts.subs_matched} matched to parts</span>
                <span className="border border-border bg-secondary px-2 py-1 text-muted-foreground">{measStaging.counts.unmatched} unmatched</span>
                <span className="ml-auto text-muted-foreground self-center">{measStaging.counts.standards} standards parsed</span>
              </div>
              <p className="text-xs text-muted-foreground">Best-effort harvest (M# → R#). Committing writes the Measure text onto each matched requirement/part. Unmatched measures are skipped. You can edit any measure afterwards per requirement.</p>
              <div className="max-h-[50vh] overflow-y-auto border border-border divide-y divide-border">
                {measStaging.items.filter((it) => it.matched).slice(0, 400).map((it, i) => (
                  <div key={i} data-testid={`meas-item-${i}`} className="px-3 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-bold text-sm">{it.standard_id}</span>
                      <span className="text-[10px] uppercase border border-emerald-300 bg-emerald-50 text-emerald-700 px-1.5 py-0.5">M{it.measure_num}</span>
                      <span className="text-[10px] text-muted-foreground">→ {it.target === "subpart" ? `part ${it.measure_num}` : it.req_number}</span>
                      {it.had_measure && <span className="text-[10px] text-amber-700">(overwrites existing)</span>}
                    </div>
                    <p className="text-xs mt-0.5 text-muted-foreground line-clamp-2">{it.text}</p>
                  </div>
                ))}
                {measStaging.items.filter((it) => it.matched).length === 0 && (
                  <div className="px-3 py-4 text-sm text-muted-foreground">No measures matched existing requirements. Check that the standards are loaded with matching IDs.</div>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setMeasStaging(null)} className="rounded-none">Cancel</Button>
                <Button data-testid="commit-measures-btn" onClick={commitMeasures} disabled={committingMeas || (measStaging.counts.matched + measStaging.counts.subs_matched === 0)}
                  className="rounded-none bg-primary hover:bg-primary/90">
                  <UploadSimple size={15} className="mr-1.5" />{committingMeas ? "Applying…" : `Apply ${measStaging.counts.matched + measStaging.counts.subs_matched} measure(s)`}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Attachment dialog */}
      <Dialog open={attDialog} onOpenChange={setAttDialog}>
        <DialogContent className="rounded-none max-w-xl">
          <DialogHeader><DialogTitle>{attEditId ? "Edit" : "Add"} Attachment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Label</Label><Input data-testid="att-label-input" value={attForm.label} onChange={(e) => setAttForm({ ...attForm, label: e.target.value })} placeholder="Attachment 1" className="rounded-none mt-1" /></div>
              <div><Label>Effective Date</Label><Input data-testid="att-date-input" type="date" value={attForm.effective_date} onChange={(e) => setAttForm({ ...attForm, effective_date: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div><Label>Title</Label><Input data-testid="att-title-input" value={attForm.title} onChange={(e) => setAttForm({ ...attForm, title: e.target.value })} placeholder="Required Controls for Low Impact BES Cyber Systems" className="rounded-none mt-1" /></div>
            <div><Label>Attachment Text</Label><Textarea data-testid="att-text-input" value={attForm.text} onChange={(e) => setAttForm({ ...attForm, text: e.target.value })} placeholder="Paste the binding Attachment language…" className="rounded-none mt-1 min-h-[160px]" /></div>
            <Button data-testid="save-att-btn" onClick={saveAtt} className="rounded-none w-full bg-primary hover:bg-primary/90">Save Attachment</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Single-standard PDF review */}
      <Dialog open={!!stdReview} onOpenChange={(o) => { if (!o) setStdReview(null); }}>
        <DialogContent className="rounded-none max-w-3xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Review Parsed Standard — {stdReview?.filename}</DialogTitle></DialogHeader>
          {stdReview && (() => { const p = stdReview.parsed; return (
            <div className="space-y-4" data-testid="std-pdf-review">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="border border-border bg-secondary px-2 py-1">Parsed by: {stdReview.engine === "ai" ? "AI" : "pattern match"}</span>
                <span className="border border-emerald-300 bg-emerald-50 text-emerald-700 px-2 py-1">{p.requirements.length} requirement(s)</span>
                <span className="border border-border bg-secondary px-2 py-1">{p.attachments.length} attachment(s)</span>
                {stdReview.exists && <span className="border border-orange-400 bg-orange-50 text-orange-700 px-2 py-1 font-semibold" data-testid="std-exists-warning">{p.standard_id} already exists</span>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Standard Number</Label><Input data-testid="review-std-id" value={p.standard_id} onChange={(e) => patchParsed({ standard_id: e.target.value })} className="rounded-none mt-1" /></div>
                <div><Label>Status</Label>
                  <Select value={p.status} onValueChange={(v) => patchParsed({ status: v })}>
                    <SelectTrigger data-testid="review-std-status" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Title</Label><Input data-testid="review-std-title" value={p.title} onChange={(e) => patchParsed({ title: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label>Purpose</Label><Textarea data-testid="review-std-purpose" value={p.purpose} onChange={(e) => patchParsed({ purpose: e.target.value })} className="rounded-none mt-1 min-h-[60px]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label><Input value={p.category} onChange={(e) => patchParsed({ category: e.target.value })} className="rounded-none mt-1" /></div>
                <div><Label>Effective Date</Label><Input type="date" value={p.effective_date || ""} onChange={(e) => patchParsed({ effective_date: e.target.value })} className="rounded-none mt-1" /></div>
              </div>
              <div><Label>Applicable Functions (codes — comma separated)</Label>
                <Input data-testid="review-std-functions" value={Array.isArray(p.applicable_functions) ? p.applicable_functions.join(", ") : p.applicable_functions}
                  onChange={(e) => patchParsed({ applicable_functions: e.target.value })} placeholder="GO, GO-2" className="rounded-none mt-1" />
                {p.category2_ibr && <span data-testid="cat2-badge" className="inline-block mt-1 text-[10px] uppercase tracking-wide border border-purple-400 bg-purple-50 text-purple-700 px-1.5 py-0.5 font-bold">Category-2 IBR detected → GO-2/GOP-2 added</span>}
              </div>
              {(p.applicability || p.category2_ibr) && (
                <div><Label>Applicability (Section 4 — functional entities &amp; facility thresholds)</Label>
                  <Textarea data-testid="review-std-applicability" value={p.applicability || ""} onChange={(e) => patchParsed({ applicability: e.target.value })} className="rounded-none mt-1 min-h-[60px]" /></div>
              )}

              <div className="border-t border-border pt-3">
                <div className="overline text-muted-foreground mb-2">Requirements</div>
                <div className="space-y-3">
                  {p.requirements.map((r, i) => (
                    <div key={i} data-testid={`review-req-${i}`} className="border border-border p-3 space-y-2 bg-secondary/20">
                      <div className="flex items-center gap-2">
                        <Input data-testid={`review-req-num-${i}`} value={r.req_number} onChange={(e) => patchReq(i, { req_number: e.target.value })} className="rounded-none w-24 font-mono" />
                        <Input value={r.vrf || ""} onChange={(e) => patchReq(i, { vrf: e.target.value })} placeholder="VRF" className="rounded-none w-28" />
                        <Input value={r.time_horizon || ""} onChange={(e) => patchReq(i, { time_horizon: e.target.value })} placeholder="Time Horizon" className="rounded-none flex-1" />
                        <button data-testid={`review-req-remove-${i}`} onClick={() => removeReq(i)} className="text-muted-foreground hover:text-red-600"><Trash size={16} /></button>
                      </div>
                      <Textarea data-testid={`review-req-text-${i}`} value={r.text} onChange={(e) => patchReq(i, { text: e.target.value })} placeholder="Requirement language" className="rounded-none min-h-[70px]" />
                      <Textarea value={r.measure || ""} onChange={(e) => patchReq(i, { measure: e.target.value })} placeholder="Measure (M#) — evidence expectation" className="rounded-none min-h-[50px]" />
                      {(r.sub_requirements || []).length > 0 && (
                        <div className="text-xs text-muted-foreground">{r.sub_requirements.length} sub-part(s): {r.sub_requirements.map((s) => s.label).filter(Boolean).join(", ")}</div>
                      )}
                    </div>
                  ))}
                  {p.requirements.length === 0 && <div className="text-sm text-muted-foreground">No requirements parsed.</div>}
                </div>
              </div>

              {p.guidance && (
                <div className="border-t border-border pt-3">
                  <Label>Guidance / Guidelines &amp; Technical Basis</Label>
                  <Textarea data-testid="review-guidance" value={p.guidance} onChange={(e) => patchParsed({ guidance: e.target.value })} className="rounded-none mt-1 min-h-[80px]" />
                </div>
              )}

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox data-testid="review-overwrite" checked={stdReview.overwrite !== false} onCheckedChange={(v) => setStdReview((s) => ({ ...s, overwrite: !!v }))} />
                Overwrite if it already exists (replaces its requirements with the parsed set)
              </label>

              <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-background pt-2">
                <Button variant="outline" onClick={() => setStdReview(null)} className="rounded-none">Cancel</Button>
                <Button data-testid="commit-standard-pdf-btn" onClick={commitStandardPdf} disabled={committingStd || !p.standard_id || p.requirements.length === 0}
                  className="rounded-none bg-primary hover:bg-primary/90">
                  <UploadSimple size={15} className="mr-1.5" />{committingStd ? "Saving…" : (stdReview.exists ? "Update Standard" : "Create Standard")}
                </Button>
              </div>
            </div>
          ); })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
